// ReferralsPage — Personal referral program (different from affiliate page)
// User gets a unique link, shares with friends, earns bonuses
// Layout:
//   - Top nav: < Back | "Referrals"
//   - Hero: total earned + referral link
//   - Stats: total referrals, active, pending
//   - Share section: copy link + share to social
//   - Referral list with status
//   - Bonus structure

import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { UserAvatar, MerchantLogo, PartnerLogo } from '../../components/Brand';

interface Referral {
  id: string;
  name: string;
  email: string;
  status: 'active' | 'pending' | 'expired';
  joined: string;
  earned: number;
  tier: 'Beginner' | 'Mid' | 'Professional';
}

const REFERRALS: Referral[] = [
  { id: 'r1', name: 'Aisha Khan',     email: 'aisha@email.com',   status: 'active',  joined: '2026-06-12', earned: 75,  tier: 'Mid' },
  { id: 'r2', name: 'Diego Rivera',   email: 'diego@email.com',   status: 'active',  joined: '2026-05-28', earned: 50,  tier: 'Beginner' },
  { id: 'r3', name: 'Yuki Tanaka',    email: 'yuki@email.com',    status: 'active',  joined: '2026-05-15', earned: 100, tier: 'Professional' },
  { id: 'r4', name: 'Lena Voss',      email: 'lena@email.com',    status: 'pending', joined: '2026-07-20', earned: 0,   tier: 'Beginner' },
  { id: 'r5', name: 'Ravi Patel',     email: 'ravi@email.com',    status: 'expired', joined: '2026-04-10', earned: 0,   tier: 'Beginner' },
];

const SOCIAL = [
  { name: 'discord',  label: 'Discord' },
  { name: 'telegram', label: 'Telegram' },
  { name: 'x',        label: 'X / Twitter' },
  { name: 'instagram',label: 'Instagram' },
];

export function ReferralsPage() {
  const nav = useNavigate();
  const [copied, setCopied] = useState(false);
  const link = 'funderpro.live/r/BrandonL';
  const active = REFERRALS.filter((r) => r.status === 'active');
  const totalEarned = REFERRALS.reduce((sum, r) => sum + r.earned, 0);

  function copy() {
    navigator.clipboard?.writeText('https://' + link);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  }

  return (
    <div className="min-h-screen bg-tf-surface-1 pb-8">
      <div className="sticky top-0 z-10 flex h-11 items-center justify-between border-b border-tf-border ios26-navbar px-2 backdrop-blur-xl">
        <button onClick={() => nav(-1)} data-testid="back" className="flex h-9 w-9 items-center justify-center text-tf-blue haptic">
          <svg viewBox="0 0 24 24" className="h-5 w-5" fill="none" stroke="currentColor" strokeWidth="2.4"><path d="M15 5l-7 7 7 7" strokeLinecap="round" strokeLinejoin="round" /></svg>
        </button>
        <div className="absolute left-1/2 -translate-x-1/2 text-[15px] font-semibold">Referrals</div>
        <button className="flex h-9 w-9 items-center justify-center text-tf-blue">
          <svg viewBox="0 0 24 24" className="h-5 w-5" fill="none" stroke="currentColor" strokeWidth="1.8"><circle cx="12" cy="12" r="9" /><path d="M12 8v8M8 12h8" strokeLinecap="round" /></svg>
        </button>
      </div>

      {/* Hero — total earned */}
      <div className="mx-4 mt-4">
        <div className="rounded-[16px] border border-tf-border bg-white p-5 text-center">
          <div className="text-[11px] uppercase tracking-wider text-tf-text-3">Total earned</div>
          <div className="mt-1 text-[40px] font-bold text-tf-profit tabular-nums">${totalEarned}</div>
          <div className="mt-1 text-[13px] text-tf-text-3">from {active.length} active referrals</div>
        </div>
      </div>

      {/* Stats */}
      <div className="mx-4 mt-3 grid grid-cols-3 gap-2">
        <StatTile label="Total" value={REFERRALS.length.toString()} sub="invited" />
        <StatTile label="Active" value={active.length.toString()} sub="trading" color="#34C759" />
        <StatTile label="Pending" value={REFERRALS.filter((r) => r.status === 'pending').length.toString()} sub="joined" color="#FF9500" />
      </div>

      {/* Share section */}
      <h2 className="mt-5 px-4 pb-2 text-[13px] font-normal uppercase tracking-wide text-tf-text-3">Your referral link</h2>
      <div className="mx-4 rounded-[16px] border border-tf-border bg-white p-3">
        <div className="flex items-center gap-2 rounded-[10px] bg-tf-surface-1 px-3 py-2.5">
          <span className="text-[15px] text-tf-text-1 tabular-nums">https://{link}</span>
        </div>
        <button
          onClick={copy}
          data-testid="copy-link"
          className="mt-2 flex h-12 w-full items-center justify-center gap-2 rounded-full bg-tf-text-1 text-[15px] font-semibold text-white active:scale-[0.98] transition"
        >
          {copied ? (
            <>
              <svg viewBox="0 0 24 24" className="h-5 w-5" fill="none" stroke="currentColor" strokeWidth="2.5"><path d="M5 12l5 5L20 7" strokeLinecap="round" strokeLinejoin="round" /></svg>
              Copied to clipboard
            </>
          ) : (
            <>
              <svg viewBox="0 0 24 24" className="h-5 w-5" fill="none" stroke="currentColor" strokeWidth="2"><rect x="8" y="8" width="13" height="13" rx="2" /><path d="M16 8V5a2 2 0 0 0-2-2H5a2 2 0 0 0-2 2v9a2 2 0 0 0 2 2h3" strokeLinecap="round" /></svg>
              Copy link
            </>
          )}
        </button>

        {/* Social share */}
        <div className="mt-3 flex items-center justify-center gap-2.5">
          {SOCIAL.map((s) => (
            <button
              key={s.name}
              onClick={() => alert('Share to ' + s.label)}
              data-testid={`share-${s.name}`}
              className="flex h-12 w-12 items-center justify-center rounded-[14px] glass active:scale-95 transition"
            >
              <MerchantLogo name={s.name} size={28} />
            </button>
          ))}
        </div>
      </div>

      {/* Bonus structure */}
      <h2 className="mt-5 px-4 pb-2 text-[13px] font-normal uppercase tracking-wide text-tf-text-3">How it works</h2>
      <div className="mx-4 overflow-hidden rounded-[16px] border border-tf-border bg-white">
        <BonusRow icon="🎁" title="$30 platform activation bonus" sub="When they activate their first platform" amount={30} />
        <BonusRow icon="💰" title="20% of their first challenge fee" sub="Up to $100 per referral" amount={100} />
        <BonusRow icon="🚀" title="$50 if they go funded" sub="Within their first 30 days" amount={50} />
        <BonusRow icon="♾️" title="Lifetime 10% of their fees" sub="No cap, no expiry" amount="10%" last />
      </div>

      {/* Referral list */}
      <h2 className="mt-5 px-4 pb-2 text-[13px] font-normal uppercase tracking-wide text-tf-text-3">Your referrals</h2>
      <div className="mx-4 overflow-hidden rounded-[16px] border border-tf-border bg-white">
        {REFERRALS.length === 0 ? (
          <div className="p-6 text-center text-[14px] text-tf-text-3">No referrals yet. Share your link to get started.</div>
        ) : REFERRALS.map((r, i) => (
          <div
            key={r.id}
            data-testid={`referral-${r.id}`}
            className={['flex items-center gap-3 px-4 py-3', i < REFERRALS.length - 1 ? 'border-b border-tf-border' : ''].join(' ')}
          >
            <UserAvatar size={40} name={r.name} />
            <div className="flex-1 min-w-0">
              <div className="text-[14px] font-semibold text-tf-text-1 truncate">{r.name}</div>
              <div className="text-[11px] text-tf-text-3 truncate">{r.email}</div>
              <div className="text-[11px] text-tf-text-3">Joined {new Date(r.joined).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}</div>
            </div>
            <div className="text-right">
              <span className={['rounded-full px-2 py-0.5 text-[10px] font-bold uppercase',
                r.status === 'active' ? 'bg-tf-green-tint text-tf-profit' :
                r.status === 'pending' ? 'bg-tf-orange-tint text-tf-warning' :
                'bg-tf-surface-3 text-tf-text-3'].join(' ')}>
                {r.status}
              </span>
              {r.earned > 0 && (
                <div className="mt-0.5 text-[12px] font-semibold tabular-nums text-tf-profit">+${r.earned}</div>
              )}
            </div>
          </div>
        ))}
      </div>

      {/* Powered by */}
      <div className="mx-4 mt-4 px-2">
        <div className="text-[10px] uppercase tracking-wider text-tf-text-3 text-center mb-2">Referral payouts via</div>
        <div className="flex items-center justify-center gap-4 opacity-80">
          <PartnerLogo name="stripe" height={22} />
          <PartnerLogo name="coinbase" height={22} />
        </div>
      </div>
    </div>
  );
}

function StatTile({ label, value, sub, color }: { label: string; value: string; sub: string; color?: string }) {
  return (
    <div className="rounded-[12px] border border-tf-border bg-white p-3 text-center">
      <div className="text-[11px] uppercase tracking-wide text-tf-text-3">{label}</div>
      <div className="mt-1 text-[20px] font-bold tabular-nums" style={{ color: color || '#1C1C1E' }}>{value}</div>
      <div className="text-[10px] text-tf-text-3">{sub}</div>
    </div>
  );
}

function BonusRow({ icon, title, sub, amount, last }: { icon: string; title: string; sub: string; amount: number | string; last?: boolean }) {
  return (
    <div className={['flex items-center gap-3 px-4 py-3', last ? '' : 'border-b border-tf-border'].join(' ')}>
      <div className="flex h-9 w-9 items-center justify-center rounded-[10px] bg-tf-green-tint text-[18px]">{icon}</div>
      <div className="flex-1 min-w-0">
        <div className="text-[14px] font-semibold text-tf-text-1">{title}</div>
        <div className="text-[11px] text-tf-text-3">{sub}</div>
      </div>
      <div className="text-[14px] font-bold tabular-nums text-tf-profit">{typeof amount === 'number' ? '$' + amount : amount}</div>
    </div>
  );
}
